import React from "react";
import { Collapse, Divider, Tag, Tooltip } from "antd";
import { useSelector } from "react-redux";
// Enums — top level
import { EncodedMessageContent } from "../../../flatbuffers/generated/ln2/encoded-message-content";
import { BitMaskMessageContent } from "../../../flatbuffers/generated/ln2/bit-mask-message-content";
import { OneHotMessageContent } from "../../../flatbuffers/generated/ln2/one-hot-message-content";
// Traffic
import { ElevatorTraffic } from "../../../flatbuffers/generated/traffic/elevator-traffic";
import { EscalatorTraffic } from "../../../flatbuffers/generated/traffic/escalator-traffic";
// Usage
import { ElevatorUsage } from "../../../flatbuffers/generated/usage/elevator-usage";
import { EscalatorUsage } from "../../../flatbuffers/generated/usage/escalator-usage";
// Service
import { ElevatorService } from "../../../flatbuffers/generated/service/elevator-service";
import { EscalatorService } from "../../../flatbuffers/generated/service/escalator-service";
// Control
import { ElevatorControl } from "../../../flatbuffers/generated/control/elevator-control";
import { EscalatorControl } from "../../../flatbuffers/generated/control/escalator-control";
// Fault
import { ElevatorFault } from "../../../flatbuffers/generated/fault/elevator-fault";
import { EscalatorFault } from "../../../flatbuffers/generated/fault/escalator-fault";
import { FaultContent } from "../../../flatbuffers/generated/fault/fault-content";
// Group supervisory
import { ElevatorGroupSupervisoryStatus } from "../../../flatbuffers/generated/group-supervisory-status/elevator-group-supervisory-status";
import { EscalatorGroupSupervisoryStatus } from "../../../flatbuffers/generated/group-supervisory-status/escalator-group-supervisory-status";
// Group control
import { ElevatorGroupControl } from "../../../flatbuffers/generated/group-control/elevator-group-control";
import { EscalatorGroupControl } from "../../../flatbuffers/generated/group-control/escalator-group-control";
// Status
import { ElevatorStatusContent } from "../../../flatbuffers/generated/status/elevator-status-content";
import { EscalatorStatusContent } from "../../../flatbuffers/generated/status/escalator-status-content";
import { ConnectionStatus } from "../../../flatbuffers/generated/status/connection-status";
import { DoorStatus } from "../../../flatbuffers/generated/status/door-status";
import { ModeStatus } from "../../../flatbuffers/generated/status/mode-status";
import { TrafficStatus } from "../../../flatbuffers/generated/status/traffic-status";
import { LoadWeigherStatus } from "../../../flatbuffers/generated/status/load-weigher-status";
import { EarthquakeStatus } from "../../../flatbuffers/generated/status/earthquake-status";
import { EarthquakeProtectionStatus } from "../../../flatbuffers/generated/status/earthquake-protection-status";
import { Status } from "../../../flatbuffers/generated/status/status";
// Controller info
import { ElevatorControllerInfoContent } from "../../../flatbuffers/generated/controller-info/elevator-controller-info-content";
import { EscalatorControllerInfoContent } from "../../../flatbuffers/generated/controller-info/escalator-controller-info-content";
// Device type
import { DevType } from "../../../flatbuffers/generated/front-end/dev-type";
// Device content (union discriminators)
import { DeviceStatusContent } from "../../../flatbuffers/generated/status/device-status-content";
import { DeviceUsageContent } from "../../../flatbuffers/generated/usage/device-usage-content";
import { DeviceServiceContent } from "../../../flatbuffers/generated/service/device-service-content";
import { DeviceGroupSupervisoryStatusContent } from "../../../flatbuffers/generated/group-supervisory-status/device-group-supervisory-status-content";
import { DeviceGroupControlContent } from "../../../flatbuffers/generated/group-control/device-group-control-content";
import { DeviceControllerInfoContent } from "../../../flatbuffers/generated/controller-info/device-controller-info-content";
// INTERFACES
import { RootState } from "../../../app/store";

// ─── Types & helpers ──────────────────────────────────────────────────────────

type EnumRow = { value: number; name: string };

function enumToRows(enumObj: object): EnumRow[] {
  return Object.entries(enumObj)
    .filter(([key]) => Number.isNaN(Number(key)))
    .map(([key, value]) => ({ name: key, value: value as number }))
    .sort((a, b) => a.value - b.value);
}

// ─── EnumPills ────────────────────────────────────────────────────────────────

function EnumPills({ data }: { data: EnumRow[] }) {
  const { theme } = useSelector((state: RootState) => state.home);

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {data.map(({ value, name }) => (
        <Tooltip key={value} title={name}>
          <div
            className={`generalStyles__box__${theme} generalStyles__textEllipsis`}
            style={{
              alignItems: "center",
              width: "200px",
              cursor: "pointer",
              borderRadius: 4,
              padding: "5px",
              fontSize: 11,
            }}
          >
            <Tag color="blue" style={{ margin: 0, fontSize: 10, lineHeight: "16px" }}>
              {value}
            </Tag>
            <span style={{ marginLeft: "10px" }}>{name}</span>
          </div>
        </Tooltip>
      ))}
    </div>
  );
}

// ─── SubSection ───────────────────────────────────────────────────────────────

function SubSection({ label, data }: { label: string; data: EnumRow[] }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>{label}</div>
      <EnumPills data={data} />
    </div>
  );
}

// ─── SideBySide ───────────────────────────────────────────────────────────────

function SideBySide({
  elevatorContent,
  escalatorContent,
}: {
  elevatorContent: React.ReactNode;
  escalatorContent: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", gap: 0 }}>
      <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, color: "#1890ff" }}>Elevator</div>
        {elevatorContent}
      </div>
      <Divider type="vertical" style={{ height: "auto", margin: "0 4px" }} />
      <div style={{ flex: 1, minWidth: 0, paddingLeft: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, color: "#52c41a" }}>Escalator</div>
        {escalatorContent}
      </div>
    </div>
  );
}

// ─── Status column ────────────────────────────────────────────────────────────

const STATUS_SHARED_SUBSECTIONS = [
  { label: "Connection Status", data: enumToRows(ConnectionStatus) },
  { label: "Mode Status", data: enumToRows(ModeStatus) },
  { label: "Door Status", data: enumToRows(DoorStatus) },
  { label: "Traffic Status", data: enumToRows(TrafficStatus) },
  { label: "Load Weigher Status", data: enumToRows(LoadWeigherStatus) },
  { label: "Earthquake Status", data: enumToRows(EarthquakeStatus) },
  { label: "Earthquake Protection Status", data: enumToRows(EarthquakeProtectionStatus) },
  { label: "General Status", data: enumToRows(Status) },
];

function StatusColumn({ statusContent }: { statusContent: EnumRow[] }) {
  return (
    <>
      <SubSection label="Status Sub-types" data={statusContent} />
      {STATUS_SHARED_SUBSECTIONS.map((s) => (
        <SubSection key={s.label} label={s.label} data={s.data} />
      ))}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const { Panel } = Collapse;

export default function FlatbuffersReferencePage() {
  const { theme } = useSelector((state: RootState) => state.home);

  return (
    <div className={`layout__contentPage__${theme}`}>
      <div style={{ marginBottom: 12 }}>
        <strong style={{ marginRight: 8 }}>contentType:</strong>
        {enumToRows(EncodedMessageContent).map(({ value, name }) => (
          <Tag key={name} color="orange" style={{ marginRight: 4 }}>
            {value} = {name}
          </Tag>
        ))}
      </div>
      <div style={{ marginBottom: 12 }}>
        <strong style={{ marginRight: 8 }}>devType:</strong>
        {enumToRows(DevType).map(({ value, name }) => (
          <Tag key={name} color="purple" style={{ marginRight: 4 }}>
            {value} = {name}
          </Tag>
        ))}
      </div>

      <Collapse>
        <Panel
          header={`Traffic — messageType ${BitMaskMessageContent.Traffic_TrafficMessage} (BitMask)`}
          key="traffic"
        >
          <SideBySide
            elevatorContent={<EnumPills data={enumToRows(ElevatorTraffic)} />}
            escalatorContent={<EnumPills data={enumToRows(EscalatorTraffic)} />}
          />
        </Panel>

        <Panel
          header={`Status — messageType ${BitMaskMessageContent.Status_StatusMessage} (BitMask)`}
          key="status"
        >
          <SubSection label="deviceContent" data={enumToRows(DeviceStatusContent)} />
          <SideBySide
            elevatorContent={<StatusColumn statusContent={enumToRows(ElevatorStatusContent)} />}
            escalatorContent={<StatusColumn statusContent={enumToRows(EscalatorStatusContent)} />}
          />
        </Panel>

        <Panel
          header={`Control — messageType ${BitMaskMessageContent.Control_ControlMessage} (BitMask)`}
          key="control"
        >
          <SideBySide
            elevatorContent={<EnumPills data={enumToRows(ElevatorControl)} />}
            escalatorContent={<EnumPills data={enumToRows(EscalatorControl)} />}
          />
        </Panel>

        <Panel
          header={`Fault — messageType ${BitMaskMessageContent.Fault_FaultMessage} (BitMask)`}
          key="fault"
        >
          <SubSection label="deviceContent" data={enumToRows(FaultContent)} />
          <SideBySide
            elevatorContent={<EnumPills data={enumToRows(ElevatorFault)} />}
            escalatorContent={<EnumPills data={enumToRows(EscalatorFault)} />}
          />
        </Panel>

        <Panel
          header={`Group Supervisory — messageType ${BitMaskMessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage} (BitMask)`}
          key="groupSupervisory"
        >
          <SubSection
            label="deviceContent"
            data={enumToRows(DeviceGroupSupervisoryStatusContent)}
          />
          <SideBySide
            elevatorContent={<EnumPills data={enumToRows(ElevatorGroupSupervisoryStatus)} />}
            escalatorContent={<EnumPills data={enumToRows(EscalatorGroupSupervisoryStatus)} />}
          />
        </Panel>

        <Panel
          header={`Group Control — messageType ${BitMaskMessageContent.GroupControl_GroupControlMessage} (BitMask)`}
          key="groupControl"
        >
          <SubSection label="deviceContent" data={enumToRows(DeviceGroupControlContent)} />
          <SideBySide
            elevatorContent={<EnumPills data={enumToRows(ElevatorGroupControl)} />}
            escalatorContent={<EnumPills data={enumToRows(EscalatorGroupControl)} />}
          />
        </Panel>

        <Panel
          header={`Controller Info — messageType ${BitMaskMessageContent.ControllerInfo_ControllerInfoMessage} (BitMask)`}
          key="controllerInfo"
        >
          <SubSection label="deviceContent" data={enumToRows(DeviceControllerInfoContent)} />
          <SideBySide
            elevatorContent={<EnumPills data={enumToRows(ElevatorControllerInfoContent)} />}
            escalatorContent={<EnumPills data={enumToRows(EscalatorControllerInfoContent)} />}
          />
        </Panel>

        <Panel
          header={`Usage — messageType ${OneHotMessageContent.Usage_UsageMessage} (OneHot)`}
          key="usage"
        >
          <SubSection label="deviceContent" data={enumToRows(DeviceUsageContent)} />
          <SideBySide
            elevatorContent={<EnumPills data={enumToRows(ElevatorUsage)} />}
            escalatorContent={<EnumPills data={enumToRows(EscalatorUsage)} />}
          />
        </Panel>

        <Panel
          header={`Service — messageType ${OneHotMessageContent.Service_ServiceMessage} (OneHot)`}
          key="service"
        >
          <SubSection label="deviceContent" data={enumToRows(DeviceServiceContent)} />
          <SideBySide
            elevatorContent={<EnumPills data={enumToRows(ElevatorService)} />}
            escalatorContent={<EnumPills data={enumToRows(EscalatorService)} />}
          />
        </Panel>
      </Collapse>
    </div>
  );
}
